import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { getNotificationCounts } from '../api/notifications';
import useNotificationStream from '../hooks/useNotificationStream';

// ─── Context ──────────────────────────────────────────────────────────────────

const NotificationContext = createContext({
  unread:          0,
  critical:        0,
  latestItems:     [],   // up to 20 most recent notifications for the popover
  markCountsRead:  () => {},
  decrementUnread: () => {},
  prependItem:     () => {},
});

export function useNotifications() {
  return useContext(NotificationContext);
}

// ─── Provider — mount once inside DashboardLayout ────────────────────────────

export function NotificationProvider({ children }) {
  const [unread,      setUnread]      = useState(0);
  const [critical,    setCritical]    = useState(0);
  // Holds the last ≤20 items pushed by SSE so the popover can show them instantly
  const [latestItems, setLatestItems] = useState([]);

  // Sync counts from a full-list API response (called when popover opens)
  const syncCounts = useCallback((payload) => {
    setUnread(payload.unread    ?? payload.notViewed ?? 0);
    setCritical(payload.critical ?? 0);
  }, []);

  // Called by the popover after mark-all-read
  const markCountsRead = useCallback(() => {
    setUnread(0);
    setCritical(0);
    setLatestItems((prev) => prev.map((n) => ({ ...n, isViewed: true })));
  }, []);

  // Called by the popover after marking a single item read
  const decrementUnread = useCallback((id, wasCritical) => {
    setUnread((c) => Math.max(0, c - 1));
    if (wasCritical) setCritical((c) => Math.max(0, c - 1));
    setLatestItems((prev) =>
      prev.map((n) => (n.id === id ? { ...n, isViewed: true } : n))
    );
  }, []);

  // Called by popover to inject its freshly-fetched list into the shared state
  const prependItem = useCallback((notification) => {
    setLatestItems((prev) => {
      if (prev.some((n) => n.id === notification.id)) return prev;
      return [notification, ...prev].slice(0, 20);
    });
  }, []);

  // ── SSE connection lives here — one connection for the whole dashboard ────
  useNotificationStream({
    onNotification: useCallback(({ notification, counts }) => {
      setLatestItems((prev) => {
        if (prev.some((n) => n.id === notification.id)) return prev;
        return [notification, ...prev].slice(0, 20);
      });
      if (counts) {
        setUnread(counts.unread    ?? 0);
        setCritical(counts.critical ?? 0);
      }
    }, []),

    onCounts: useCallback((counts) => {
      setUnread(counts.unread    ?? 0);
      setCritical(counts.critical ?? 0);
    }, []),

    // Fallback poll when SSE is unavailable
    onFallbackPoll: useCallback(async () => {
      try {
        const res    = await getNotificationCounts();
        const counts = res?.data ?? res;
        setUnread(counts.unread    ?? 0);
        setCritical(counts.critical ?? 0);
      } catch (_) { /* counts fetch failure is non-critical */ }
    }, []),
  });

  return (
    <NotificationContext.Provider
      value={{ unread, critical, latestItems, syncCounts, markCountsRead, decrementUnread, prependItem }}
    >
      {children}
    </NotificationContext.Provider>
  );
}
